import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import colors from "assets/theme-dark/base/colors";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";

interface DeleteCubeDialogProps {
  open: boolean;
  cubeName: string | null;
  onClose: () => void;
  onDelete: () => Promise<void>;
  deleteLoading: boolean;
}

function DeleteCubeDialog({ open, cubeName, onClose, onDelete, deleteLoading }: DeleteCubeDialogProps): JSX.Element {
  const isCubeNameLong = cubeName && cubeName.length > 30;
  const displayedCubeName = isCubeNameLong ? `${cubeName.slice(0, 30)}...` : cubeName || "this cube";

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      component={motion.div}
    >
      <StyledDialogTitle id="alert-dialog-title">
        <MDTypography variant="h6" fontWeight="bold" color="text">
          Confirm Cube Deletion
        </MDTypography>
      </StyledDialogTitle>
      <StyledDialogContent>
        <DialogContentText id="alert-dialog-description">
          <MDTypography variant="body2" color="text" sx={{ lineHeight: 1.6 }}>
            Are you sure you want to delete the cube{" "}
            {isCubeNameLong ? (
              <Tooltip title={cubeName!} placement="top">
                <strong>{displayedCubeName}</strong>
              </Tooltip>
            ) : (
              <strong>{displayedCubeName}</strong>
            )}
            ? This action cannot be undone.
          </MDTypography>
        </DialogContentText>
      </StyledDialogContent>
      <StyledDialogActions>
        <MDButton
          onClick={onClose}
          variant="outlined"
          sx={{
            color: colors.text.main,
            borderColor: colors.grey[700],
            borderWidth: "1px",
            borderStyle: "solid",
            padding: "8px 16px",
            borderRadius: "8px",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: colors.grey[300],
              borderColor: colors.grey[600],
              transform: "translateY(-1px)",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
            },
            "&:active": {
              transform: "translateY(0)",
              boxShadow: "none",
            },
          }}
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={onDelete}
          variant="contained"
          disabled={deleteLoading}
          sx={{
            background: `linear-gradient(45deg, ${colors.error.main} 30%, ${colors.error.focus} 90%)`,
            color: colors.white.main,
            padding: "8px 16px",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
            transition: "all 0.2s ease",
            "&:hover": {
              background: `linear-gradient(45deg, ${colors.error.focus} 30%, ${colors.error.main} 90%)`,
              transform: "translateY(-1px)",
              boxShadow: "0 6px 12px rgba(0, 0, 0, 0.4)",
            },
            "&:active": {
              transform: "translateY(0)",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
            },
            "&:disabled": {
              background: colors.grey[500],
              color: colors.grey[300],
              boxShadow: "none",
            },
          }}
        >
          {deleteLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <MDTypography variant="button" fontWeight="medium">
              Delete
            </MDTypography>
          )}
        </MDButton>
      </StyledDialogActions>
    </StyledDialog>
  );
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: `linear-gradient(145deg, ${colors.background.card} 0%, ${colors.background.default} 100%)`,
    borderRadius: "12px",
    color: colors.text.main,
    minWidth: "320px",
    maxWidth: "480px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2)",
    overflow: "hidden",
    transition: "all 0.3s ease",
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: colors.background.default,
  color: colors.text.main,
  fontWeight: "bold",
  padding: "16px 24px",
  display: "flex",
  alignItems: "center",
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: "20px 24px",
  background: colors.background.card,
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: "16px 24px",
  background: colors.background.default,
  display: "flex",
  gap: "12px",
  justifyContent: "flex-end",
}));

export default DeleteCubeDialog;
